
import React, { useState, useEffect, useContext, createContext } from 'react';
import { cn } from '@/lib/utils';
import { imageLoadStats } from '@/components/OptimizedImage';

// Context to track global page loading state
interface LoadingContextType {
  isLoading: boolean;
  progress: number;
  startLoading: () => void;
  completeLoading: () => void;
  setProgress: (value: number) => void;
}

const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  progress: 0,
  startLoading: () => {},
  completeLoading: () => {},
  setProgress: () => {},
});

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const startLoading = () => {
    // Skip showing loading state, immediately complete
    setProgress(100);
    completeLoading();
  };

  const completeLoading = () => {
    // No animation delays, just complete
    setIsLoading(false);
    setProgress(0);
  };

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        progress,
        startLoading,
        completeLoading,
        setProgress,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

// A lightweight component that monitors route changes and data loading
export const RouteChangeMonitor: React.FC = () => {
  const { startLoading, completeLoading } = useLoading();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  // Monitor route changes
  useEffect(() => {
    let isPageChanging = false;
    
    const checkLocation = () => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
        if (!isPageChanging) {
          isPageChanging = true;
          // Immediately complete loading
          startLoading();
          completeLoading();
          isPageChanging = false;
        }
      }
    };
    
    // Check location on regular intervals
    const intervalId = setInterval(checkLocation, 100);
    
    // Clean up
    return () => {
      clearInterval(intervalId);
    };
  }, [currentPath, startLoading, completeLoading]);
  
  return null; // This is a monitoring component, no UI needed
};

// The actual progress bar component - now disabled/hidden
const ProgressiveLoader: React.FC<{
  className?: string;
  barColor?: string;
}> = () => {
  // Return null to completely remove the loader
  return null;
};

export default ProgressiveLoader;
